/**
 * Feature E03-F03 — "Kickoff enrichment + two-pass transcript prefill" contracts.
 *
 * 1:1 port of the legacy n8n `ai_request` sub-actions:
 *   - kickoff_enrichment        (group Kickoff — docs/source-n8n/reference/Kickoff.md)
 *   - prefill_questionnaire      (group PF     — docs/source-n8n/reference/PF.md)
 *   - search_website_for_missing (group WS     — docs/source-n8n/reference/WS.md)
 * Canonical prompts: docs/source-n8n/ai_prompts.md
 *   (kickoff_enrichment / prefill_questionnaire / prefill_questionnaire_transcript /
 *    search_website_for_missing).
 *
 * Single source of truth for:
 *   - POST /projects/:id/ai/kickoff_enrichment       (T01 service + T04 route)
 *   - POST /projects/:id/ai/prefill_questionnaire     (T02/T03 service + T04 route)
 *   - POST /projects/:id/ai/search_website_for_missing (T06 service + T04 route)
 *   - the SPA response shapes (project-kickoff.js runKickoffAi / prefillQuestionnaire
 *     / searchCompanyWebsite).
 *
 * IDOR invariant (carried over unchanged from `DB: Validate Session1`): `userId`
 * is NEVER part of any input schema — the api derives the actor from the validated
 * session, and projectId comes from the URL (the legacy nodes passed `body.userId`
 * / `body.projectId` through with no ownership check — every group's gotcha).
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent); the
 * serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Prompt keys (prompts-only-from-DB — loaded from ai_prompts, never hard-coded)
// ---------------------------------------------------------------------------
/** prompt_key for kickoff_enrichment (Kickoff: Load Prompt — is_active=true). */
export const KICKOFF_ENRICHMENT_PROMPT_KEY = 'kickoff_enrichment';
/** prompt_key for prefill v1 (PF: Load Prompt — legacy OMITS is_active filter). */
export const PREFILL_QUESTIONNAIRE_PROMPT_KEY = 'prefill_questionnaire';
/** prompt_key for prefill v2/transcript pass (Section 32; ai_prompts has a row). */
export const PREFILL_TRANSCRIPT_PROMPT_KEY = 'prefill_questionnaire_transcript';
/** prompt_key for the website domain web-search (WS: Load Prompt — is_active=true). */
export const SEARCH_WEBSITE_PROMPT_KEY = 'search_website_for_missing';
/**
 * Public email providers EXCLUDED from the contact_email → company-domain fallback
 * (WS: Prepare Data step 4). Lower-cased; a contact at one of these never yields a
 * company domain.
 */
export const PUBLIC_EMAIL_DOMAINS = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'icloud.com',
    'mail.ru',
    'yandex.ru',
];
/** Hard cap on concatenated Drive file text before it goes to the LLM (PF: Merge). */
export const PREFILL_FILE_CONTENT_LIMIT = 50_000;
// ---------------------------------------------------------------------------
// kickoff_enrichment (Kickoff group) — T01
// ---------------------------------------------------------------------------
/**
 * The structured JSON the kickoff_enrichment prompt returns (ai_prompts.md §34.2).
 * Every section may be `null` when the model had no data for it (Kickoff: Parse &
 * Update step 4 — `parsed.X || null`). `.passthrough()` keeps any extra keys the
 * model adds so we never silently drop enrichment detail.
 */
export const kickoffEnrichmentResultSchema = z.object({
    company_profile: z.record(z.unknown()).nullable().default(null),
    position: z.record(z.unknown()).nullable().default(null),
    requirements: z.record(z.unknown()).nullable().default(null),
    compensation: z.record(z.unknown()).nullable().default(null),
    hiring_process: z.record(z.unknown()).nullable().default(null),
    sourcing: z.record(z.unknown()).nullable().default(null),
    summary: z
        .union([z.string(), z.record(z.unknown())])
        .nullable()
        .default(null),
    missing_info: z.array(z.string()).default([]),
    /** App-side ISO timestamp (`new Date().toISOString()` — NOT a DB default). */
    enriched_at: z.string(),
});
/**
 * POST /projects/:id/ai/kickoff_enrichment success body
 * (Kickoff: Return Result — `{ success, enrichment, projectId }`).
 */
export const kickoffEnrichmentSuccessSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    enrichment: kickoffEnrichmentResultSchema,
    projectId: z.number().int(),
});
/** Failure body (missing_prompt / unparseable AI response — both HTTP 200, parity). */
export const kickoffEnrichmentFailureSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    projectId: z.number().int().optional(),
});
export const kickoffEnrichmentResponseSchema = z.union([
    kickoffEnrichmentSuccessSchema,
    kickoffEnrichmentFailureSchema,
]);
// ---------------------------------------------------------------------------
// prefill_questionnaire (PF group) — T02/T03
// ---------------------------------------------------------------------------
/**
 * POST /projects/:id/ai/prefill_questionnaire success body
 * (PF: Return Result — `{ success, answers, extracted_*, confidence_notes }`).
 * `answers` is a map question-text → final merged answer; the service has ALREADY
 * persisted the merged questionnaire (the SPA refreshes the project after).
 */
export const prefillQuestionnaireSuccessSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /** question text → merged answer (the source returned this map). */
    answers: z.record(z.string()).default({}),
    /** Company name the model extracted from the documents (header auto-fill). */
    extracted_company: z.string().nullable().optional(),
    /** Contact person name extracted from the documents. */
    extracted_contact: z.string().nullable().optional(),
    /** Contact email extracted from the documents. */
    extracted_contact_email: z.string().nullable().optional(),
    /** Free-text confidence notes the model emitted (shown to the recruiter). */
    confidence_notes: z.string().nullable().optional(),
    projectId: z.number().int().optional(),
});
/** Failure body: `no_questions` / `missing_prompt` (PF: Build Prompt graceful errors). */
export const prefillQuestionnaireFailureSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    projectId: z.number().int().optional(),
});
export const prefillQuestionnaireResponseSchema = z.union([
    prefillQuestionnaireSuccessSchema,
    prefillQuestionnaireFailureSchema,
]);
// ---------------------------------------------------------------------------
// search_website_for_missing (WS group) — T06
// ---------------------------------------------------------------------------
/**
 * POST /projects/:id/ai/search_website_for_missing body
 * (WS: Return Result — `{ success:true, filled_count, domain, message }`). The node
 * ALWAYS returns success:true (even on skip → filled_count:0); the SPA branches on
 * `filled_count`. `skipped`/`reason` are added in v2 so the UI can show "No company
 * domain" / "No unanswered questions" explicitly (acceptance #2).
 */
export const websiteSearchResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    filled_count: z.number().int().nonnegative().default(0),
    domain: z.string().default(''),
    message: z.string().default('Website search completed'),
    /** true when the search was a no-op (no domain or no unanswered questions). */
    skipped: z.boolean().optional(),
    /** Human reason for a skip ('No unanswered questions' | 'No company domain'). */
    reason: z.string().optional(),
    projectId: z.number().int().optional(),
});
// ---------------------------------------------------------------------------
// company_website_facts (E03-F02-T11) — the REAL "Search Company Website"
// ---------------------------------------------------------------------------
/**
 * The company profile the cheap model extracts from the client's OWN website.
 *
 * Same field set as the api-side `CompanyFacts` (services/preKickoffGrounding.ts,
 * E03-F02-T10) — this action reuses that pipeline verbatim (free scrape →
 * Haiku link-select → Haiku facts, never `web_search`), it only surfaces the
 * result to the recruiter instead of feeding it to the analysis prompt.
 * snake_case here because the object is persisted to `projects.data.company_facts`
 * alongside the other legacy-shaped bags.
 */
export const companyWebsiteFactsSchema = z.object({
    /** The site we actually downloaded (resolved from clients.website / domain). */
    domain: z.string().default(''),
    company_name: z.string().default(''),
    industry: z.string().default(''),
    company_type: z.string().default(''),
    offices: z.string().default(''),
    products: z.string().default(''),
    size: z.string().default(''),
    founded: z.string().default(''),
    summary: z.string().default(''),
    /** Free-form supporting facts (already sanitized + capped api-side). */
    facts: z.array(z.string()).default([]),
    /** Provenance — the pages actually read, shown under the facts. */
    page_urls: z.array(z.string()).default([]),
    /** ISO timestamp of the run (rendered as the panel's "collected" date). */
    collected_at: z.string().default(''),
    /** True when the site text came from the shared `site_text` cache (no HTTP). */
    from_cache: z.boolean().default(false),
});
/**
 * Machine-readable reason of a negative (but successful) website lookup. The SPA
 * branches on the CODE, never on the wording:
 *   - `COMPANY_FACTS_PROJECT_NOT_FOUND` — no such project for this actor;
 *   - `COMPANY_FACTS_NO_WEBSITE`        — nothing to scrape: neither
 *     `clients.website`/`clients.domain` nor a corporate contact email yields a
 *     domain (zero-spend exit — the recruiter must add the site on Pre-Kickoff);
 *   - `COMPANY_FACTS_SITE_UNREACHABLE`  — the site was tried but produced nothing
 *     usable (unreachable / robots-disallowed / empty text / unparsable answer);
 *   - `COMPANY_FACTS_NO_PROVIDER`       — no LLM provider configured (admin task);
 *   - `COMPANY_FACTS_DISABLED`          — the client-site scraping kill switch is
 *     off for this environment.
 */
export const COMPANY_FACTS_FAILURE_CODES = {
    projectNotFound: 'COMPANY_FACTS_PROJECT_NOT_FOUND',
    noWebsite: 'COMPANY_FACTS_NO_WEBSITE',
    siteUnreachable: 'COMPANY_FACTS_SITE_UNREACHABLE',
    noProvider: 'COMPANY_FACTS_NO_PROVIDER',
    disabled: 'COMPANY_FACTS_DISABLED',
};
export const companyFactsFailureCodeSchema = z.enum([
    'COMPANY_FACTS_PROJECT_NOT_FOUND',
    'COMPANY_FACTS_NO_WEBSITE',
    'COMPANY_FACTS_SITE_UNREACHABLE',
    'COMPANY_FACTS_NO_PROVIDER',
    'COMPANY_FACTS_DISABLED',
]);
/**
 * Human wording per failure code — the API sends it and the SPA toasts it
 * verbatim, so the recruiter never sees a raw machine token (the pre-fix
 * behaviour: the toast text was the `error` code string itself).
 */
export const COMPANY_FACTS_FAILURE_MESSAGES = {
    COMPANY_FACTS_PROJECT_NOT_FOUND: 'Project not found',
    COMPANY_FACTS_NO_WEBSITE: 'No company website known — add it on the Pre-Kickoff screen first',
    COMPANY_FACTS_SITE_UNREACHABLE: 'Could not read the company website (unreachable or nothing to extract)',
    COMPANY_FACTS_NO_PROVIDER: 'No AI provider configured — ask an admin',
    COMPANY_FACTS_DISABLED: 'Website lookup is disabled for this environment',
};
/** POST /projects/:id/ai/company_website_facts success body. */
export const companyWebsiteFactsSuccessSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    /**
     * Discriminant twin of `found` on the salary contract (E12-F06-T04). `success`
     * cannot carry it: the web client strips a top-level `success:true`
     * (`unwrapLegacySuccess`), so the flag is gone by the time the SPA parses the
     * body — hence `optional()` here and a REQUIRED `collected:false` on the
     * negative arm. `collected` is never stripped.
     */
    collected: z.literal(true).optional(),
    facts: companyWebsiteFactsSchema,
    projectId: z.number().int().optional(),
});
/**
 * Negative response (the lookup ran, no facts came back) — HTTP 200.
 *
 * "No website known" / "site unreachable" are legitimate BUSINESS OUTCOMES of a
 * successful request, not transport failures, so they keep HTTP 200 — but they
 * MUST NOT wear the legacy `{ success:false, error }` envelope. That envelope is
 * exactly what `isLegacyErrorPayload` (shared/legacy.ts) detects and what the web
 * client (`toApiResult`) demotes to an `ApiResult` ERROR on a 200 body. The
 * consequences (the same trap E12-F06-T04 removed from the salary route):
 *   - the negative outcome became indistinguishable from a real network/5xx
 *     failure at the `ApiResult` level;
 *   - this schema's branch was UNREACHABLE in the SPA — the body never reached
 *     `onSuccess`, so `if (res.success === false)` in `KickoffView` was dead code
 *     and every consumer had to know the trap and dig the wording out of
 *     `error.detail.message` inside `onError`.
 *
 * The v2 shape therefore carries NO `success` flag; it is discriminated by the
 * required `collected:false` and carries a {@link CompanyFactsFailureCode} plus
 * the human `message`:
 *
 *     200 { collected:false, code:'COMPANY_FACTS_NO_WEBSITE', message:'No company website known — …', projectId }
 *
 * `error` is kept as an OPTIONAL deprecated alias so a rolling deploy (old bundle
 * ↔ new API) keeps parsing; new producers set `message`.
 */
export const companyWebsiteFactsFailureSchema = z.object({
    collected: z.literal(false),
    code: companyFactsFailureCodeSchema,
    message: z.string().min(1),
    /** @deprecated legacy alias of {@link message}; historical payloads only. */
    error: z.string().optional(),
    projectId: z.number().int().optional(),
});
/**
 * The full route response — success OR negative (both HTTP 200). Discriminated
 * STRUCTURALLY: the success arm requires `facts`, the negative arm requires
 * `collected:false` + `code`, so neither arm can parse the other's body and the
 * order of the union is irrelevant.
 */
export const companyWebsiteFactsResponseSchema = z.union([
    companyWebsiteFactsSuccessSchema,
    companyWebsiteFactsFailureSchema,
]);
/**
 * Type guard for the negative arm — the ONE way a consumer should branch. Never
 * test `success === false` (the web client strips `success:true` and the API no
 * longer emits `success:false`) and never match on the message text.
 */
export function isCompanyWebsiteFactsFailure(res) {
    return res.collected === false;
}
/** Build the canonical negative body for a reason (single producer helper). */
export function companyWebsiteFactsFailure(code, projectId) {
    return {
        collected: false,
        code,
        message: COMPANY_FACTS_FAILURE_MESSAGES[code],
        ...(projectId === undefined ? {} : { projectId }),
    };
}
// ---------------------------------------------------------------------------
// Shared request body (all three actions; identity-free — IDOR rule)
// ---------------------------------------------------------------------------
/**
 * The (optional) request body for every kickoff-AI action. Intentionally empty of
 * identity: `projectId` is the URL param and the actor is the session. A
 * `session_token` may ride in the body for legacy transport parity but is NEVER
 * read for authorization. For prefill, the SPA pre-saves notes/transcript via the
 * separate update_project call before invoking this route (project-kickoff.js).
 */
/**
 * B3/KS-10 — which of the two questionnaire passes the caller is asking for.
 *
 * `prefill` (default) is the PRE-call action: fill blanks from the documents,
 * the intro email and the manual context, never touching what a human typed.
 * `post_meeting` is the POST-call action behind "AI Processing": the meeting
 * notes and the transcript are the freshest and most authoritative source — they
 * came from the real conversation — so they OVERRIDE existing answers on
 * conflict, while document-derived guesses still only fill blanks.
 */
export const prefillModeSchema = z.enum(['prefill', 'post_meeting']);
export const kickoffAiRequestSchema = z
    .object({
    session_token: z.string().min(1).optional(),
    mode: prefillModeSchema.optional(),
})
    .partial();
//# sourceMappingURL=kickoff-enrichment.js.map